import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { HelmetProvider } from "react-helmet-async";
import { GlobalErrorBoundary } from "@/components/ErrorBoundary";

const isResizeObserverNoise = (message) => (
  typeof message === "string" &&
  (
    message.includes("ResizeObserver loop completed with undelivered notifications") ||
    message.includes("ResizeObserver loop limit exceeded")
  )
);

// Also suppress cross-origin "Script error." from third-party widgets (TradingView)
const isCrossOriginScriptError = (message) => (
  message === "Script error." || message === "Script error"
);

const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  const msg = String(message || "");
  if (isResizeObserverNoise(msg) || isCrossOriginScriptError(msg)) {
    return true; // returning true prevents the error from propagating
  }
  if (typeof originalOnError === "function") {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

window.addEventListener("error", (event) => {
  const msg = String(event.message || "");
  if (isResizeObserverNoise(msg) || isCrossOriginScriptError(msg)) {
    event.preventDefault();
    event.stopImmediatePropagation(); // stops CRA overlay from seeing it
  }
}, true); // capture phase = runs BEFORE any other listeners

window.addEventListener("unhandledrejection", (event) => {
  const message = event.reason?.message || String(event.reason || "");
  if (isResizeObserverNoise(message)) {
    event.preventDefault();
  }
});

if (typeof window.ResizeObserver !== "undefined") {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>,
);
